import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, ROLES, ROLE_LABELS } from '../../context/AppContext';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { StatusTracker } from '../../components/shared/StatusTracker';
import {
  STATUS_CONFIG,
  CASE_STATUSES,
  defaultPositiveVerificationQuestions,
  defaultNegativeVerificationQuestions,
  defaultPositiveRequiredImages,
  defaultNegativeRequiredImages,
} from '../../data/mockData';
import { fetchCases, updateCaseAction } from '../../api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Phone,
  Navigation,
  Camera,
  MapPin,
  Clock,
  CheckCircle2,
  IndianRupee,
  Route,
  Upload,
  Send,
  X,
  RefreshCw,
  Eye,
  Play,
  History,
  Compass,
  Check,
  Calendar,
  AlertTriangle,
  AlertCircle,
  Map,
  CheckCircle,
  XCircle,
  Trash2,
  FileCheck,
  ArrowLeft,
  ChevronRight,
  Grid,
  List,
  Building,
  Mail,
  FileText,
  ShieldCheck,
  ChevronDown,
  Save,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { format } from 'date-fns';

const priorityColors = {
  high: 'bg-red-50 text-red-700 border-red-200 font-semibold text-xs',
  medium: 'bg-amber-50 text-amber-700 border-amber-200 font-semibold text-xs',
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold text-xs',
};

export default function FEModule() {
  const {
    currentRole,
    currentUserId,
    extraCostsList,
    submitExtraCostRequest,
    caseAssignments,
    rejectCaseNonWorkingArea,
    penaltiesList,
    updateCaseStatus,
    caseStatusOverrides,
    submitReturnRequest,
    caseReturnRequestsList,
    addNotification,
  } = useApp();
  
  const navigate = useNavigate();
  const isVendorFE = currentRole === ROLES.VENDOR_FE;
  
  const [activeTab, setActiveTab] = useState('cases');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCase, setSelectedCase] = useState(null);

  // Verification screen/outcomes state
  const [feScreen, setFeScreen] = useState(1); // 1 = Main Dashboard List, 2 = Case Details Screen, 3 = Dynamic Verification Form
  const [outcomeMode, setOutcomeMode] = useState('positive'); // 'positive' or 'negative'
  const [formAnswers, setFormAnswers] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]);
  const [feRemarks, setFeRemarks] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);

  // Geotagging Editor Modal States
  const [geotagModalOpen, setGeotagModalOpen] = useState(false);
  const [activeGeoImageIdx, setActiveGeoImageIdx] = useState(null);
  const [tempLat, setTempLat] = useState('23.0225');
  const [tempLng, setTempLng] = useState('72.5714');

  // Drafts Persistent State
  const [draftsMap, setDraftsMap] = useState(() => {
    try {
      const stored = localStorage.getItem('fe_verification_drafts');
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      return {};
    }
  });

  const saveDraftToStorage = (updatedDrafts) => {
    setDraftsMap(updatedDrafts);
    try {
      localStorage.setItem('fe_verification_drafts', JSON.stringify(updatedDrafts));
    } catch (e) {
      console.error(e);
    }
  };
  
  // Extra Cost Modal States
  const [showExtraCostModal, setShowExtraCostModal] = useState(false);
  const [extraCostCase, setExtraCostCase] = useState(null);
  const [expenseType, setExpenseType] = useState('Fuel Charges');
  const [requestedAmount, setRequestedAmount] = useState('');
  const [extraCostReason, setExtraCostReason] = useState('');
  const [evidenceFileName, setEvidenceFileName] = useState('');

  // Return Case Modal States
  const [showReturnRequestModal, setShowReturnRequestModal] = useState(false);
  const [returnCase, setReturnCase] = useState(null);
  const [returnReason, setReturnReason] = useState('Outside Serviceable Area');
  const [returnComments, setReturnComments] = useState('');

  // Route Planning states
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  const [showRoutePlanner, setShowRoutePlanner] = useState(false);
  const [routeStops, setRouteStops] = useState([]);
  const [isGeneratingRoute, setIsGeneratingRoute] = useState(false);
  
  // Extra KMs dialog
  const [showKmDialog, setShowKmDialog] = useState(false);
  const [extraKmRequest, setExtraKmRequest] = useState('');
  const [extraKmReason, setExtraKmReason] = useState('');

  const activeUser = (() => {
    try {
      const stored = localStorage.getItem('crm_user');
      if (stored) return JSON.parse(stored);
    } catch(e) {}
    return { id: currentUserId, name: 'Field Executive', role: currentRole };
  })();

  const [liveCases, setLiveCases] = useState([]);
  useEffect(() => {
    async function load() {
      try {
        const data = await fetchCases();
        if (Array.isArray(data)) setLiveCases(data);
      } catch (err) {
        setLiveCases([]);
      }
    }
    load();
  }, []);

  // Dynamic Case Filters based on assignments and unassigned actions
  const myAssignedCases = useMemo(() => {
    return liveCases.filter(c => {
      const isAssigned = c.assignedTo?.id === currentUserId || c.assignedTo_id === currentUserId;
      const isNotRejected = caseAssignments[c.id] !== 'unassigned';
      return isAssigned && isNotRejected;
    });
  }, [liveCases, currentUserId, caseAssignments]);

  // Out TAT Cases calculation
  const myOutTATCases = useMemo(() => {
    return myAssignedCases.filter(c => {
      const isOverdue = new Date(c.dueDate) < new Date();
      const isNotClosed = ![
        CASE_STATUSES.COMPLETED,
        CASE_STATUSES.REPORT_SENT,
        CASE_STATUSES.QC_APPROVED
      ].includes(c.status);
      return isOverdue && isNotClosed;
    });
  }, [myAssignedCases]);

  const myPendingCases = useMemo(() => {
    return myAssignedCases.filter(c => 
      [CASE_STATUSES.ASSIGNED, CASE_STATUSES.IN_PROGRESS].includes(c.status)
    );
  }, [myAssignedCases]);

  const myClosedCases = useMemo(() => {
    return myAssignedCases.filter(c => 
      [CASE_STATUSES.VERIFIED, CASE_STATUSES.QC_PENDING, CASE_STATUSES.COMPLETED, CASE_STATUSES.REPORT_SENT].includes(c.status)
    );
  }, [myAssignedCases]);

  // Earnings calculation (FE role only)
  const getPayoutForCase = (c) => {
    const basePrice = c.product?.basePrice || 250;
    return Math.round(basePrice * 0.6); // 60% of base rate
  };

  const baseEarnings = myClosedCases.reduce((sum, c) => sum + getPayoutForCase(c), 0);
  const myExtraCosts = extraCostsList.filter(r => r.requestedBy?.id === currentUserId);
  const pendingExtraCostsAmount = myExtraCosts.filter(r => r.status === 'pending').reduce((sum, r) => sum + r.amount, 0);
  const approvedExtraCostsAmount = myExtraCosts.filter(r => r.status === 'approved').reduce((sum, r) => sum + r.amount, 0);
  const myPenaltiesAmount = penaltiesList.filter(p => p.userId === currentUserId && p.status === 'Deducted').reduce((sum, p) => sum + p.amount, 0);
  const finalPayableAmount = Math.max(0, baseEarnings + approvedExtraCostsAmount - myPenaltiesAmount);

  // Filter cases dynamically by selected status filter
  const filteredCases = useMemo(() => {
    return myAssignedCases.filter(c => {
      if (statusFilter === 'all') return true;
      if (statusFilter === 'pending') return c.status === CASE_STATUSES.ASSIGNED;
      if (statusFilter === 'in_progress') return c.status === CASE_STATUSES.IN_PROGRESS;
      if (statusFilter === 'completed') {
        return [CASE_STATUSES.VERIFIED, CASE_STATUSES.COMPLETED, CASE_STATUSES.REPORT_SENT].includes(c.status);
      }
      if (statusFilter === 'qc_pending') return c.status === CASE_STATUSES.QC_PENDING;
      if (statusFilter === 'out_tat') {
        const isOverdue = new Date(c.dueDate) < new Date();
        const isNotClosed = ![CASE_STATUSES.COMPLETED, CASE_STATUSES.REPORT_SENT, CASE_STATUSES.QC_APPROVED].includes(c.status);
        return isOverdue && isNotClosed;
      }
      return true;
    });
  }, [myAssignedCases, statusFilter]);

  // Dashboard Stats Cards
  const stats = [
    { label: 'Pending Cases', value: myPendingCases.length, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border border-amber-100' },
    { label: 'Closed Cases', value: myClosedCases.length, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50 border border-emerald-100' },
    { label: 'Out TAT Cases', value: myOutTATCases.length, icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50 border border-red-100' },
    { label: 'Today Visits', value: myPendingCases.filter(c => c.priority === 'high' || new Date(c.dueDate).toDateString() === new Date().toDateString()).length, icon: Calendar, color: 'text-purple-600', bg: 'bg-purple-50 border border-purple-100' },
    { label: 'Route Distance', value: `${myPendingCases.length * 4.5 + 8} km`, icon: Route, color: 'text-blue-600', bg: 'bg-blue-50 border border-blue-100' },
  ];

  // Route Planning Generation
  const handlePlanRoute = () => {
    if (selectedCaseIds.length === 0) {
      window.alert('Please select at least one pending case using the checkboxes first.');
      return;
    }
    
    setIsGeneratingRoute(true);
    setShowRoutePlanner(true);
    
    // Simulate smart optimized sequence calculation
    setTimeout(() => {
      const casesToPlan = myAssignedCases.filter(c => selectedCaseIds.includes(c.id));
      const stops = casesToPlan.map((c, index) => ({
        sequence: index + 1,
        applicantName: c.applicantName,
        address: c.address,
        refNumber: c.refNumber,
        eta: format(new Date(Date.now() + (index + 1) * 45 * 60 * 1000), 'hh:mm a'),
        distance: `${((index + 1) * 3.8).toFixed(1)} km`,
      }));
      setRouteStops(stops);
      setIsGeneratingRoute(false);
    }, 1000);
  };

  const toggleSelectCase = (caseId, e) => {
    e.stopPropagation();
    setSelectedCaseIds(prev =>
      prev.includes(caseId) ? prev.filter(id => id !== caseId) : [...prev, caseId]
    );
  };

  const handleSelectCase = (c) => {
    setSelectedCase(c);
    setFeScreen(2); // Go to Case Details Screen
    
    // Check if there's a saved draft
    const savedDraft = draftsMap[c.id];
    if (savedDraft) {
      toast.info('Restored form details from saved draft.');
    }
  };

  const handleStartVerification = () => {
    setShowOutcomeModal(true);
  };

  const handleOutcomeSelect = (mode) => {
    setOutcomeMode(mode);
    setShowOutcomeModal(false);
    
    // Check if there's a draft saved for this case
    const savedDraft = draftsMap[selectedCase.id];
    if (savedDraft && savedDraft.outcomeMode === mode) {
      setFormAnswers(savedDraft.formAnswers || {});
      setUploadedImages(savedDraft.uploadedImages || []);
      setFeRemarks(savedDraft.feRemarks || '');
      setInternalNotes(savedDraft.internalNotes || '');
    } else {
      // Initialize empty form state
      setFormAnswers({});
      
      // Initialize uploaded images list based on verification defaults
      const defaultImages = mode === 'positive' ? defaultPositiveRequiredImages : defaultNegativeRequiredImages;
      const requiredImages = defaultImages.map(img => ({
        label: img.label,
        required: img.required,
        geoTagRequired: img.geoTag,
        file: null,
        previewUrl: '',
        lat: '23.0225',
        lng: '72.5714',
        timestamp: '',
        address: selectedCase.address || 'Address location'
      }));
      setUploadedImages(requiredImages);
      setFeRemarks('');
      setInternalNotes('');
    }
    
    setFeScreen(3); // Go to Dynamic Verification Form Screen
  };

  const handleOutcomeChange = (mode) => {
    setOutcomeMode(mode);
    
    const defaultImages = mode === 'positive' ? defaultPositiveRequiredImages : defaultNegativeRequiredImages;
    const requiredImages = defaultImages.map(img => ({
      label: img.label,
      required: img.required,
      geoTagRequired: img.geoTag,
      file: null,
      previewUrl: '',
      lat: '23.0225',
      lng: '72.5714',
      timestamp: '',
      address: selectedCase.address || 'Address location'
    }));
    setUploadedImages(requiredImages);
    setFormAnswers({});
    toast.success(`Swapped to ${mode === 'positive' ? 'Positive Outcome Form' : 'Negative Outcome Form'}`);
  };

  const handleInputChange = (questionId, value) => {
    setFormAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleCameraCapture = (index) => {
    const positivePics = [
      'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400',
      'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400',
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=400',
      'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400'
    ];
    
    const randomPic = positivePics[Math.floor(Math.random() * positivePics.length)];
    const now = new Date();
    const timestamp = now.toLocaleString();
    
    const updatedImages = [...uploadedImages];
    updatedImages[index] = {
      ...updatedImages[index],
      previewUrl: randomPic,
      timestamp: timestamp,
      lat: tempLat || '23.0225',
      lng: tempLng || '72.5714',
      address: selectedCase.address || '123, Verification Location'
    };
    
    setUploadedImages(updatedImages);
    toast.success(`Simulated photo captured with GPS Telemetry & Watermark!`);
  };

  const handleFileUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const now = new Date();
      const timestamp = now.toLocaleString();
      const previewUrl = URL.createObjectURL(file);
      
      const updatedImages = [...uploadedImages];
      updatedImages[index] = {
        ...updatedImages[index],
        file: file,
        previewUrl: previewUrl,
        timestamp: timestamp,
        lat: tempLat || '23.0225',
        lng: tempLng || '72.5714',
        address: selectedCase.address || '123, Verification Location'
      };
      setUploadedImages(updatedImages);
      toast.success(`Uploaded ${file.name} successfully!`);
    }
  };

  const removeImage = (index) => {
    const updatedImages = [...uploadedImages];
    updatedImages[index] = {
      ...updatedImages[index],
      file: null,
      previewUrl: '',
      timestamp: ''
    };
    setUploadedImages(updatedImages);
    toast.info('Image removed.');
  };

  const openGeotagModal = (index) => {
    setActiveGeoImageIdx(index);
    setTempLat(uploadedImages[index].lat || '23.0225');
    setTempLng(uploadedImages[index].lng || '72.5714');
    setGeotagModalOpen(true);
  };

  const saveGeotagDetails = () => {
    if (activeGeoImageIdx !== null) {
      const updatedImages = [...uploadedImages];
      updatedImages[activeGeoImageIdx] = {
        ...updatedImages[activeGeoImageIdx],
        lat: tempLat,
        lng: tempLng
      };
      setUploadedImages(updatedImages);
      setGeotagModalOpen(false);
      toast.success(`Updated geotag specs for ${uploadedImages[activeGeoImageIdx].label}!`);
    }
  };

  const handleMapGridClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const baseLat = 23.0225;
    const baseLng = 72.5714;
    const computedLat = (baseLat + (100 - y) * 0.00015).toFixed(6);
    const computedLng = (baseLng + (x - 150) * 0.00015).toFixed(6);
    
    setTempLat(computedLat);
    setTempLng(computedLng);
    toast.info(`Telemetry pin placed at: Lat ${computedLat}, Lng ${computedLng}`);
  };

  const handleSaveDraft = () => {
    if (!selectedCase) return;
    const draftData = {
      caseId: selectedCase.id,
      outcomeMode,
      formAnswers,
      uploadedImages,
      feRemarks,
      internalNotes,
      savedAt: new Date().toLocaleString()
    };
    
    const updatedDrafts = { ...draftsMap, [selectedCase.id]: draftData };
    saveDraftToStorage(updatedDrafts);
    toast.success(`Draft saved successfully for Lead ${selectedCase.id}`);
  };

  const activeQuestions = useMemo(() => {
    if (outcomeMode === 'positive') {
      return defaultPositiveVerificationQuestions;
    } else {
      return defaultNegativeVerificationQuestions;
    }
  }, [outcomeMode]);

  const handleSubmitVerification = () => {
    if (!selectedCase) return;
    
    // Check if required images are captured/uploaded
    const missingImages = uploadedImages.filter(img => img.required && !img.previewUrl);
    if (missingImages.length > 0) {
      toast.error(`Please provide required images: ${missingImages.map(m => m.label).join(', ')}`);
      return;
    }

    // Check if required form questions are answered
    const missingQuestions = activeQuestions.filter(q => q.required && !formAnswers[q.id]);
    if (missingQuestions.length > 0) {
      toast.error(`Please answer required fields: ${missingQuestions.map(m => m.fieldLabel).join(', ')}`);
      return;
    }

    // Enforce minimum image validation based on Verification Type or Product Name
    const verifType = (selectedCase.verificationType || selectedCase.product?.name || '').toLowerCase();
    const totalCapturedImages = uploadedImages.filter(img => img.previewUrl).length;
    
    if (verifType.includes('residence') || verifType.includes('address')) {
      if (totalCapturedImages < 5) {
        toast.error(`Residence Verification requires a minimum of 5 images. Current: ${totalCapturedImages}. Please upload or capture more images.`);
        return;
      }
    } else if (verifType.includes('office') || verifType.includes('employment')) {
      if (totalCapturedImages < 4) {
        toast.error(`Office Verification requires a minimum of 4 images. Current: ${totalCapturedImages}. Please upload or capture more images.`);
        return;
      }
    } else if (verifType.includes('shop') || verifType.includes('business')) {
      if (totalCapturedImages < 6) {
        toast.error(`Shop Verification requires a minimum of 6 images. Current: ${totalCapturedImages}. Please upload or capture more images.`);
        return;
      }
    }

    // Update global state and persist answers to mockData object in memory
    const answersBlock = activeQuestions.map(q => ({
      question: q.fieldLabel,
      answer: formAnswers[q.id] || 'N/A'
    }));

    // Persist verification updates to backend
    try {
      updateCaseAction(selectedCase.id, {
        status: CASE_STATUSES.QC_PENDING,
        verificationStatus: outcomeMode,
        answers: answersBlock,
        feRemark: feRemarks,
        verifiedImages: uploadedImages.filter(img => img.previewUrl).map(img => ({
          label: img.label,
          url: img.previewUrl,
          latitude: parseFloat(img.lat),
          longitude: parseFloat(img.lng),
          timestamp: img.timestamp,
          address: img.address
        }))
      }).catch(err => console.warn('Backend update error:', err));
    } catch (e) {
      console.warn('Backend update failed:', e);
    }

    // Also call context status update
    updateCaseStatus(selectedCase.id, CASE_STATUSES.QC_PENDING);

    // Delete saved drafts for this case
    const updatedDrafts = { ...draftsMap };
    delete updatedDrafts[selectedCase.id];
    saveDraftToStorage(updatedDrafts);

    // Trigger Notification for Quality Checker
    const feName = activeUser?.name || 'Field Executive';
    
    addNotification({
      title: 'FE Verification Form Submitted',
      message: `Verification form for Case ${selectedCase.id} has been submitted for QC approval by Field Executive ${feName}.`,
      type: 'info',
      targetRole: 'quality_checker'
    });

    toast.success(`Verification completed! Case ${selectedCase.id} submitted for QC check.`);
    
    // Reset view
    setFeScreen(1);
    setSelectedCase(null);
  };

  return (
    <div className="space-y-6 animate-fade-in text-slate-800" data-testid="fe-module-page">
      {/* ------------------ SCREEN 1: FE DASHBOARD LISTING ------------------ */}
      {feScreen === 1 && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 pb-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">FE Dashboard</h1>
              <p className="text-sm text-slate-500 mt-1">{isVendorFE ? "Vendor Side Field Executive Portal" : "Field Executive Portal"} • {format(new Date(), 'EEEE, MMMM dd')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handlePlanRoute}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 font-semibold shadow-md"
                data-testid="route-plan-btn"
              >
                <Compass className="w-4 h-4" />
                Plan Route {selectedCaseIds.length > 0 && `(${selectedCaseIds.length})`}
              </Button>
            </div>
          </div>

          {/* Operational Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className={`crm-card p-4 rounded-xl ${stat.bg} shadow-2xs transition-all hover:scale-102`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/80 shadow-3xs">
                      <Icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                    <div>
                      <p className="text-xl font-black text-slate-900 tracking-tight">{stat.value}</p>
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-0.5">{stat.label}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tabs list (Earnings visible only to core Field Executive, not Vendor FE) */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-white border border-slate-200 p-1 w-full max-w-lg flex">
              <TabsTrigger value="cases" className="flex-1 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 font-semibold text-sm">
                Assigned Cases
              </TabsTrigger>
              <TabsTrigger value="extra-costs" className="flex-1 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 font-semibold text-sm">
                Extra Cost Requests
              </TabsTrigger>
              {!isVendorFE && (
                <TabsTrigger value="earnings" className="flex-1 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 font-semibold text-sm">
                  Earnings
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="cases" className="mt-6 space-y-6">
              {/* Case Status Filters */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-none">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mr-2">Filters:</span>
                {[
                  { id: 'all', label: 'All Cases' },
                  { id: 'pending', label: 'Pending' },
                  { id: 'in_progress', label: 'In Progress' },
                  { id: 'completed', label: 'Completed' },
                  { id: 'out_tat', label: 'Out TAT' },
                  { id: 'qc_pending', label: 'QC Pending' }
                ].map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setStatusFilter(filter.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border shrink-0 ${
                      statusFilter === filter.id
                        ? 'bg-blue-600 border-blue-600 text-white shadow-xs'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Cases List */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredCases.length === 0 ? (
                  <div className="col-span-2 crm-card p-12 text-center text-slate-400">
                    <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="font-semibold text-sm">No cases found matching this status filter.</p>
                  </div>
                ) : (
                  filteredCases.map((caseItem) => {
                    const isSelected = selectedCaseIds.includes(caseItem.id);
                    const verificationType = caseItem.verificationType || caseItem.product?.name || 'Residence Verification';
                    
                    return (
                      <div
                        key={caseItem.id}
                        className={`crm-card p-5 relative cursor-pointer hover-lift border transition-all ${
                          isSelected ? 'border-blue-500 bg-blue-50/20' : 'border-slate-200'
                        }`}
                        onClick={() => handleSelectCase(caseItem)}
                      >
                        {/* Checkbox for Route Planning */}
                        {['assigned', 'in_progress'].includes(caseItem.status) && (
                          <div className="absolute top-4 left-4" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => toggleSelectCase(caseItem.id, e)}
                              className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                            />
                          </div>
                        )}

                        <div className={cn("space-y-3", ['assigned', 'in_progress'].includes(caseItem.status) && "pl-6")}>
                          {/* Top Header Row */}
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-bold text-blue-600 text-sm hover:underline">{caseItem.refNumber}</p>
                              <h3 className="text-base font-bold text-slate-900 mt-1">{caseItem.applicantName}</h3>
                            </div>
                            <Badge variant="outline" className={priorityColors[caseItem.priority]}>
                              {caseItem.priority.charAt(0).toUpperCase() + caseItem.priority.slice(1)}
                            </Badge>
                          </div>

                          {/* Product and Verification Stack */}
                          <div className="grid grid-cols-2 gap-2 text-xs border-y border-slate-100 py-2">
                            <div>
                              <span className="text-slate-400 block uppercase tracking-wider font-semibold text-[9px]">Product</span>
                              <span className="text-slate-700 font-semibold">{caseItem.product?.name}</span>
                            </div>
                            <div>
                              <span className="text-slate-400 block uppercase tracking-wider font-semibold text-[9px]">Verification Type</span>
                              <span className="text-slate-700 font-semibold">{verificationType}</span>
                            </div>
                          </div>

                          {/* Address display */}
                          <div className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
                            <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                            <span className="break-words whitespace-normal">{caseItem.address}</span>
                          </div>

                          {/* Bottom row: status, due date, actions */}
                          <div className="flex items-center justify-between pt-3 border-t border-slate-100 flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              {caseStatusOverrides[caseItem.id] === 'return_pending' ? (
                                <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold uppercase rounded h-5">
                                  Return Pending
                                </Badge>
                              ) : (
                                <StatusBadge
                                  status={STATUS_CONFIG[caseItem.status]?.color || 'neutral'}
                                  label={STATUS_CONFIG[caseItem.status]?.label || caseItem.status}
                                  size="sm"
                                />
                              )}
                              <span className="text-[10px] text-slate-400 font-semibold">
                                Due: {format(new Date(caseItem.dueDate), 'MMM dd, yyyy')}
                              </span>
                            </div>

                            {/* Quick Action Buttons */}
                            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                              {/* Start Verification */}
                              {caseItem.status === CASE_STATUSES.ASSIGNED && caseStatusOverrides[caseItem.id] !== 'return_pending' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="w-7 h-7 rounded-full text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border border-emerald-100"
                                  title="Start Verification"
                                  onClick={() => handleSelectCase(caseItem)}
                                >
                                  <Play className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              
                              {/* Request Extra Cost */}
                              {['assigned', 'in_progress'].includes(caseItem.status) && caseStatusOverrides[caseItem.id] !== 'return_pending' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="w-7 h-7 rounded-full text-amber-500 hover:text-amber-600 hover:bg-amber-50 border border-amber-100"
                                  title="Request Extra Cost"
                                  onClick={() => {
                                    setExtraCostCase(caseItem);
                                    setShowExtraCostModal(true);
                                  }}
                                >
                                  <IndianRupee className="w-3.5 h-3.5" />
                                </Button>
                              )}

                              {/* Open Map */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-7 h-7 rounded-full text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-100"
                                title="Open Map Location"
                                onClick={() => {
                                  window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(caseItem.address)}`, '_blank');
                                }}
                              >
                                <Compass className="w-3.5 h-3.5" />
                              </Button>

                              {/* Call Applicant */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-7 h-7 rounded-full text-teal-600 hover:text-teal-700 hover:bg-teal-50 border border-teal-100"
                                title="Call Applicant"
                                onClick={() => {
                                  window.alert(`Calling applicant ${caseItem.applicantName} at ${caseItem.applicantPhone || '+91 98765 43210'}`);
                                }}
                              >
                                <Phone className="w-3.5 h-3.5" />
                              </Button>

                              {/* Upload Documents */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-7 h-7 rounded-full text-purple-600 hover:text-purple-700 hover:bg-purple-50 border border-purple-100"
                                title="Upload Documents"
                                onClick={() => {
                                  handleSelectCase(caseItem);
                                }}
                              >
                                <Upload className="w-3.5 h-3.5" />
                              </Button>

                              {/* View History */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-7 h-7 rounded-full text-slate-500 hover:text-slate-700 hover:bg-slate-50 border border-slate-200"
                                title="View History Timeline"
                                onClick={() => {
                                  handleSelectCase(caseItem);
                                }}
                              >
                                <History className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </TabsContent>

            {/* Extra Costs requests Tab */}
            <TabsContent value="extra-costs" className="mt-6">
              <Card className="crm-card border-slate-200 bg-white p-6 rounded-3xl shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Extra Cost Approvals Tracker</h3>
                    <p className="text-xs text-slate-500">Track the lifecycle of your travel, fuel, or special verification expenses.</p>
                  </div>
                  <span className="text-xs font-semibold text-slate-605 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-full">
                    Requests: {myExtraCosts.length}
                  </span>
                </div>
                {myExtraCosts.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm">
                    <IndianRupee className="w-10 h-10 mx-auto text-slate-350 mb-2" />
                    No extra cost requests raised yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-100">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold">
                        <tr>
                          <th className="px-4 py-3">Request ID</th>
                          <th className="px-4 py-3">Case ID</th>
                          <th className="px-4 py-3">Expense Type</th>
                          <th className="px-4 py-3">Amount</th>
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {myExtraCosts.map((req) => (
                          <tr key={req.id} className="hover:bg-slate-50/20 font-medium">
                            <td className="px-4 py-3.5 font-bold text-slate-800">{req.id}</td>
                            <td className="px-4 py-3.5 text-blue-600 font-bold">{req.caseRef}</td>
                            <td className="px-4 py-3.5 text-slate-600 capitalize">{req.expenseType || 'Travel / Distance'}</td>
                            <td className="px-4 py-3.5 font-bold text-amber-500 font-mono">₹{req.amount}</td>
                            <td className="px-4 py-3.5 text-slate-450">
                              {req.date ? format(new Date(req.date), 'dd-MMM-yyyy hh:mm a') : 'N/A'}
                            </td>
                            <td className="px-4 py-3.5">
                              <StatusBadge
                                status={
                                  req.status === 'approved' ? 'success' :
                                  req.status === 'rejected' ? 'danger' :
                                  req.status.startsWith('recommended') ? 'info' : 'warning'
                                }
                                label={
                                  req.status === 'recommended_manager' ? 'Recommended (Mgr)' :
                                  req.status === 'recommended_supreme' ? 'Recommended (SM)' :
                                  req.status.charAt(0).toUpperCase() + req.status.slice(1)
                                }
                              />
                            </td>
                            <td className="px-4 py-3.5 text-slate-500 max-w-[200px] truncate" title={req.reason}>{req.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </TabsContent>

            {!isVendorFE && (
              <TabsContent value="earnings" className="mt-6 space-y-6">
                <div className="crm-card p-6 bg-white border border-slate-200">
                  <h3 className="text-lg font-bold text-slate-900 mb-6">Earnings Summary</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-slate-100">
                      <span className="text-sm text-slate-500 font-semibold">Cases Completed</span>
                      <span className="text-sm font-bold text-slate-950">{myClosedCases.length}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-slate-100">
                      <span className="text-sm text-slate-500 font-semibold">Base Earnings</span>
                      <span className="text-sm font-bold text-emerald-600">₹{baseEarnings.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-slate-100">
                      <span className="text-sm text-slate-500 font-semibold">Extra KM (Pending Approval)</span>
                      <span className="text-sm font-bold text-amber-600">₹{pendingExtraCostsAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-slate-100">
                      <span className="text-sm text-slate-500 font-semibold">Extra KM (Approved @ ₹2/km)</span>
                      <span className="text-sm font-bold text-emerald-600">₹{approvedExtraCostsAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-slate-100">
                      <span className="text-sm text-slate-500 font-semibold">Penalties Deducted</span>
                      <span className="text-sm font-bold text-rose-600">-₹{myPenaltiesAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-t border-slate-200 mt-4">
                      <span className="text-sm font-black text-slate-900">Total Net Payable</span>
                      <span className="text-xl font-black text-emerald-600">₹{finalPayableAmount.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-6">
                    <Button variant="outline" className="w-full bg-white border-slate-200 gap-2 h-10 font-bold text-xs" onClick={() => setShowKmDialog(true)}>
                      <Navigation className="w-4 h-4" />
                      Request Extra KMs
                    </Button>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 gap-2 h-10 font-bold text-xs" onClick={() => navigate('/payouts')}>
                      <Send className="w-4 h-4" />
                      Request Payout
                    </Button>
                  </div>
                </div>

                <div className="crm-card p-6 bg-white border border-slate-200">
                  <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3 mb-4 flex items-center gap-2">
                    <IndianRupee className="w-5 h-5 text-blue-500" />
                    Extra Cost Requests
                  </h3>
                  {myExtraCosts.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-6">No extra cost requests raised yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {myExtraCosts.map((req) => (
                        <div key={req.id} className="p-3 border border-slate-100 rounded-lg bg-slate-50 flex items-center justify-between text-xs">
                          <div>
                            <p className="font-semibold text-slate-800">{req.reason}</p>
                            <p className="text-[10px] text-slate-400 mt-1">ID: {req.id} • Case: {req.caseRef}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-slate-900">₹{req.amount}</p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold mt-1 uppercase ${
                              req.status === 'approved' ? 'bg-emerald-50 text-emerald-600' :
                              req.status === 'pending' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-rose-600'
                            }`}>
                              {req.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            )}
          </Tabs>
        </>
      )}

      {/* ------------------ SCREEN 2: CASE DETAILS SCREEN ------------------ */}
      {feScreen === 2 && selectedCase && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setFeScreen(1);
                  setSelectedCase(null);
                }}
                className="p-2 border border-slate-200 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors bg-white font-semibold flex items-center justify-center"
                title="Go Back to List"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-extrabold text-slate-900">
                    Case Details: {selectedCase.refNumber}
                  </h1>
                  {draftsMap[selectedCase.id] && (
                    <span className="bg-rose-50 border border-rose-100 text-rose-600 text-[9px] font-bold px-2 py-0.5 rounded-md shadow-sm animate-pulse">
                      DRAFT PENDING
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  ID: {selectedCase.id} | Product: {selectedCase.product?.name} | Client: {selectedCase.client?.name}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={handleStartVerification}
                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-sm transition-colors"
                disabled={caseStatusOverrides[selectedCase.id] === 'return_pending'}
              >
                <Play className="w-3.5 h-3.5" />
                {draftsMap[selectedCase.id] ? 'Continue Verification' : 'Start Verification'}
              </Button>
            </div>
          </div>

          {caseStatusOverrides[selectedCase.id] === 'return_pending' && (
            <div className="flex gap-2.5 p-3.5 rounded-xl border border-amber-250 bg-amber-50/50 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">Case Return Pending Review</p>
                <p className="mt-0.5 text-slate-650 font-medium">You have requested a case return for a Non-Working Area. Verification actions are locked pending review by superiors.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-6">
              {/* Case Information */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">
                  Case Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-slate-400 block mb-0.5">Case ID</span>
                    <strong className="text-slate-800">{selectedCase.id}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block mb-0.5">Product</span>
                    <strong className="text-slate-800">{selectedCase.product?.name}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block mb-0.5">Verification Type</span>
                    <strong className="text-blue-600">{selectedCase.verificationType || selectedCase.product?.name || 'Residence Verification'}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block mb-0.5">Client Name</span>
                    <strong className="text-slate-800">{selectedCase.client?.name}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block mb-0.5">Created Date</span>
                    <strong className="text-slate-800">{selectedCase.createdAt ? format(new Date(selectedCase.createdAt), 'dd-MMM-yyyy hh:mm a') : 'N/A'}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block mb-0.5">Priority</span>
                    <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      selectedCase.priority === 'high' ? 'bg-red-50 text-red-650 border border-red-100' :
                      selectedCase.priority === 'medium' ? 'bg-amber-50 text-amber-650 border border-amber-100' :
                      'bg-emerald-50 text-emerald-650 border border-emerald-100'
                    }`}>{selectedCase.priority.toUpperCase()}</span>
                  </div>
                </div>
              </div>

              {/* Applicant Details */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">
                  Applicant Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-slate-400 block mb-0.5">Applicant Name</span>
                    <strong className="text-slate-800">{selectedCase.applicantName}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block mb-0.5">Contact Number</span>
                    <strong className="text-slate-800">{selectedCase.applicantPhone || '+91 98765 43210'}</strong>
                  </div>
                  <div className="md:col-span-2">
                    <span className="text-slate-400 block mb-0.5">Full Address</span>
                    <strong className="text-slate-800 break-words">{selectedCase.address}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block mb-0.5">Landmark</span>
                    <strong className="text-slate-800">{selectedCase.landmark || 'Near main junction'}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block mb-0.5">Pincode</span>
                    <strong className="text-slate-800">{selectedCase.pincode || '201301'}</strong>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">
                  Verification Instructions & Notes
                </h3>
                <div className="space-y-3 text-xs">
                  <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-lg">
                    <span className="font-bold text-blue-800 block mb-1">Field Guidelines</span>
                    <p className="text-slate-650 leading-normal">{selectedCase.instructions || 'Please confirm the identity of the applicant, physical existence of the address, ownership status, and capture at least 5 geolocated photos with GPS watermark coordinates. Neighbors feedback must be collected.'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="font-bold text-slate-700 block mb-1">Verification Notes</span>
                    <p className="text-slate-655 leading-normal">{selectedCase.notes || 'Note: If the door is locked or the address is not found, take a picture of the location/landmark anyway and request neighbor statements for a negative report. Do not skip geotags.'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Checklist */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">
                  Required Proofs Checklist
                </h3>
                <div className="space-y-3">
                  <div className="space-y-1.5 text-xs">
                    <span className="font-semibold text-slate-600 block">Required Images:</span>
                    <ul className="list-disc pl-4 text-slate-500 space-y-1">
                      <li>House Entrance Gate (Geotagged)</li>
                      <li>Selfie with House / Shop (Geotagged)</li>
                      <li>Landmark / Neighborhood Photo (Geotagged)</li>
                      <li>Applicant Meeting Photo (if met)</li>
                      <li>Address Proof document (e.g. Utility bill)</li>
                    </ul>
                  </div>
                  <div className="space-y-1.5 text-xs pt-2 border-t border-slate-100">
                    <span className="font-semibold text-slate-600 block">Required Documents:</span>
                    {selectedCase.documents && selectedCase.documents.length > 0 ? (
                      <div className="space-y-1">
                        {selectedCase.documents.map((doc, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 border border-slate-100 rounded-md">
                            <span className="font-medium text-slate-700 truncate max-w-[140px]">{doc.name}</span>
                            <span className="text-[10px] text-slate-400">{doc.size}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-450 italic">No document attachments provided.</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center gap-2 text-slate-800">
                  <Map className="w-5 h-5 text-blue-600" />
                  <h3 className="font-extrabold text-sm">Directions & GPS</h3>
                </div>
                <div className="h-32 border border-slate-200 rounded-xl relative overflow-hidden bg-slate-50 flex items-center justify-center border-dashed">
                  <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:16px_16px] opacity-40"></div>
                  <MapPin className="w-8 h-8 text-blue-600 fill-blue-100 animate-bounce" />
                </div>
                <Button
                  variant="outline"
                  className="w-full border-slate-200 text-slate-700 hover:bg-slate-50 gap-2 text-xs font-semibold h-9"
                  onClick={() => {
                    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedCase.address)}`, '_blank');
                  }}
                >
                  <Navigation className="w-4 h-4 text-blue-600" />
                  Open in Google Maps
                </Button>
              </div>

              {/* Action Buttons */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
                <Button 
                  disabled={caseStatusOverrides[selectedCase.id] === 'return_pending'}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2 text-xs h-9 font-semibold"
                  onClick={handleStartVerification}
                >
                  <Play className="w-4 h-4" />
                  Start Verification
                </Button>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    disabled={caseStatusOverrides[selectedCase.id] === 'return_pending'}
                    onClick={() => {
                      setExtraCostCase(selectedCase);
                      setShowExtraCostModal(true);
                    }}
                    className="border-blue-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700 gap-1 text-[11px] font-semibold h-9"
                  >
                    <IndianRupee className="w-3 h-3" />
                    Extra Cost
                  </Button>

                  <Button
                    variant="outline"
                    disabled={caseStatusOverrides[selectedCase.id] === 'return_pending'}
                    onClick={() => {
                      setReturnCase(selectedCase);
                      setShowReturnRequestModal(true);
                    }}
                    className="border-red-100 text-rose-600 hover:bg-red-50 hover:text-red-700 gap-1 text-[11px] font-semibold h-9"
                  >
                    <X className="w-3.5 h-3.5" />
                    Return Case
                  </Button>
                </div>

                <Button
                  variant="ghost"
                  onClick={() => {
                    setFeScreen(1);
                    setSelectedCase(null);
                  }}
                  className="w-full text-slate-500 hover:bg-slate-50 text-xs h-9"
                >
                  Back To Cases
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------ SCREEN 3: DYNAMIC VERIFICATION FORM ------------------ */}
      {feScreen === 3 && selectedCase && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300 text-slate-800">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFeScreen(2)}
                className="p-2 border border-slate-200 rounded-lg text-slate-500 hover:text-slate-855 hover:bg-slate-50 transition-colors bg-white font-semibold flex items-center justify-center"
                title="Go Back to Case Details"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h1 className="text-base font-extrabold text-slate-900">
                  Verification Reporting: {selectedCase.applicantName}
                </h1>
                <p className="text-xs text-slate-400 mt-0.5">
                  ID: {selectedCase.id} | Type: {selectedCase.verificationType || selectedCase.product?.name || 'Residence Verification'}
                </p>
              </div>
            </div>

            <div className="flex items-center border border-slate-200 rounded-lg p-0.5 bg-slate-100">
              <button
                type="button"
                onClick={() => handleOutcomeChange('positive')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${outcomeMode === 'positive' ? 'bg-white text-emerald-600 shadow-sm font-bold' : 'text-slate-500 hover:text-slate-800'}`}
              >
                <CheckCircle className="w-3.5 h-3.5" />
                Positive Outcome
              </button>
              <button
                type="button"
                onClick={() => handleOutcomeChange('negative')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${outcomeMode === 'negative' ? 'bg-white text-rose-600 shadow-sm font-bold' : 'text-slate-500 hover:text-slate-800'}`}
              >
                <XCircle className="w-3.5 h-3.5" />
                Negative Outcome
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Verification Summary</span>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs font-semibold">
                  <div>
                    <span className="text-slate-400 block font-normal">Case ID:</span>
                    <span className="text-slate-800">{selectedCase.id}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-normal">Applicant:</span>
                    <span className="text-slate-800">{selectedCase.applicantName}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-normal">Product:</span>
                    <span className="text-slate-800">{selectedCase.product?.name}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-normal">Verification Type:</span>
                    <span className="text-slate-800">{selectedCase.verificationType || selectedCase.product?.name || 'Residence Verification'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-normal">Outcome Selected:</span>
                    <span className={`font-bold flex items-center gap-1 ${outcomeMode === 'positive' ? 'text-emerald-650' : 'text-rose-650'}`}>
                      {outcomeMode === 'positive' ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                      {outcomeMode.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Dynamic Survey */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-6">
                <div className="border-b border-slate-100 pb-3 mb-2 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="font-extrabold text-sm text-slate-850">Survey Parameters</h3>
                    <p className="text-[10px] text-slate-400 font-medium">Please answer the dynamic questions mapped to this outcome.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeQuestions.map((q) => {
                    const ansValue = formAnswers[q.id] || '';
                    return (
                      <div key={q.id} className={`space-y-1.5 ${q.fieldType === 'textarea' ? 'md:col-span-2' : ''}`}>
                        <label className="text-xs font-bold text-slate-600 flex items-center gap-1">
                          {q.fieldLabel}
                          {q.required && <span className="text-rose-505 font-bold">*</span>}
                        </label>

                        {q.fieldType === 'text' && (
                          <Input
                            type="text"
                            value={ansValue}
                            onChange={(e) => handleInputChange(q.id, e.target.value)}
                            className="bg-slate-50/50 border-slate-200 text-xs text-slate-800 rounded-xl"
                            placeholder={`Enter ${q.fieldLabel.toLowerCase()}...`}
                          />
                        )}

                        {q.fieldType === 'number' && (
                          <Input
                            type="number"
                            value={ansValue}
                            onChange={(e) => handleInputChange(q.id, e.target.value)}
                            className="bg-slate-50/50 border-slate-200 text-xs text-slate-800 rounded-xl"
                            placeholder="0"
                          />
                        )}

                        {q.fieldType === 'date' && (
                          <input
                            type="date"
                            value={ansValue}
                            onChange={(e) => handleInputChange(q.id, e.target.value)}
                            className="w-full rounded-xl bg-slate-50/50 border border-slate-200 px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        )}

                        {q.fieldType === 'textarea' && (
                          <textarea
                            value={ansValue}
                            onChange={(e) => handleInputChange(q.id, e.target.value)}
                            rows={3}
                            className="w-full rounded-xl bg-slate-50/50 border border-slate-200 p-3 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder={`Describe details for ${q.fieldLabel.toLowerCase()}...`}
                          />
                        )}

                        {q.fieldType === 'dropdown' && (() => {
                          let options = [];
                          try {
                            options = JSON.parse(q.optionsJson || '[]');
                          } catch (e) {
                            options = ['Yes', 'No'];
                          }
                          return (
                            <select
                              value={ansValue}
                              onChange={(e) => handleInputChange(q.id, e.target.value)}
                              className="w-full rounded-xl bg-slate-50/50 border border-slate-200 px-3 py-2.5 text-xs text-slate-800 outline-none cursor-pointer focus:bg-white focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="">Select option...</option>
                              {options.map((opt, idx) => (
                                <option key={idx} value={opt}>{opt}</option>
                              ))}
                            </select>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Remarks */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
                <div className="border-b border-slate-100 pb-2">
                  <h3 className="font-bold text-slate-800 text-sm">Executive Remarks</h3>
                </div>
                <div className="space-y-4 text-xs">
                  <div className="space-y-1.5">
                    <label className="block text-slate-500 font-bold">Field Executive Remarks</label>
                    <textarea
                      value={feRemarks}
                      onChange={(e) => setFeRemarks(e.target.value)}
                      rows={3}
                      className="w-full rounded-xl bg-slate-50/50 border border-slate-200 p-3 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Add key summary comments gathered during physical visit..."
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-slate-500 font-bold">Internal Notes (Private)</label>
                    <textarea
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      rows={2}
                      className="w-full rounded-xl bg-slate-50/50 border border-slate-200 p-3 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Add internal background notes or coordinate verification notes..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Photos and Submit */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
                <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Camera className="w-5 h-5 text-blue-600" />
                    <h3 className="font-extrabold text-sm text-slate-850">Photo Evidence</h3>
                  </div>
                  <span className="text-[10px] bg-slate-100 text-slate-650 px-2 py-0.5 rounded font-bold">
                    {uploadedImages.filter(i => i.previewUrl).length} / {uploadedImages.length}
                  </span>
                </div>

                <div className="space-y-4">
                  {uploadedImages.map((img, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-xl p-3 bg-slate-50 space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <strong className="text-xs text-slate-700 block leading-tight">{img.label}</strong>
                          <span className="text-[10px] text-slate-400 mt-0.5 block">
                            {img.required ? 'Mandatory Proof' : 'Optional Evidence'}
                          </span>
                        </div>
                        {img.previewUrl && (
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="p-1 border border-slate-200 hover:border-red-200 text-slate-400 hover:text-red-500 rounded bg-white transition-colors"
                            title="Delete Image"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {img.previewUrl ? (
                        <div className="relative border border-slate-200 rounded-lg overflow-hidden h-36 bg-slate-800">
                          <img 
                            src={img.previewUrl} 
                            alt={img.label} 
                            className="w-full h-full object-cover opacity-85" 
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-transparent to-transparent flex flex-col justify-end p-2.5 space-y-0.5 select-none text-[8px] text-slate-300 font-medium">
                            <div className="flex justify-between text-[7px] text-blue-400 font-bold border-b border-slate-800/40 pb-0.5">
                              <span>AD VENTURES WATERMARK VERIFIED</span>
                              <span>GPS ACTIVE</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Coordinates:</span>
                              <span className="text-white font-bold">{img.lat}° N, {img.lng}° E</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Timestamp:</span>
                              <span className="text-white">{img.timestamp}</span>
                            </div>
                            <div className="flex justify-between truncate">
                              <span>Location:</span>
                              <span className="text-white max-w-[130px] truncate">{img.address}</span>
                            </div>
                          </div>
                          {img.geoTagRequired && (
                            <button
                              type="button"
                              onClick={() => openGeotagModal(idx)}
                              className="absolute top-2 right-2 bg-white/90 hover:bg-white text-slate-800 border border-slate-200 rounded p-1.5 shadow-sm text-[9px] font-bold flex items-center gap-1 transition-all"
                              title="Manual GPS Tagger"
                            >
                              <MapPin className="w-3 h-3 text-blue-600" />
                              Edit Geo Specs
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="border border-slate-200 border-dashed rounded-lg p-6 bg-white flex flex-col items-center justify-center gap-3 text-center">
                          <Camera className="w-8 h-8 text-slate-300" />
                          <div>
                            <span className="text-[10px] text-slate-400 font-semibold block leading-tight">
                              Drag and drop picture here
                            </span>
                            <span className="text-[9px] text-slate-300 block mt-0.5">
                              JPEG, PNG up to 10MB
                            </span>
                          </div>
                          <div className="flex gap-2 w-full">
                            <label className="flex-1 flex items-center justify-center gap-1 py-1.5 border border-slate-200 text-[10px] font-bold rounded bg-slate-50 hover:bg-slate-100 cursor-pointer text-slate-650">
                              <Upload className="w-3.5 h-3.5" />
                              Browse File
                              <input 
                                type="file" 
                                onChange={(e) => handleFileUpload(idx, e)} 
                                className="hidden" 
                                accept="image/*"
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => handleCameraCapture(idx)}
                              className="flex-1 flex items-center justify-center gap-1 py-1.5 border border-transparent text-[10px] font-bold rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
                            >
                              <Camera className="w-3.5 h-3.5" />
                              Capture
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="flex items-center justify-center gap-1.5 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-lg text-xs transition-colors shadow-sm bg-white"
                >
                  <Save className="w-3.5 h-3.5" />
                  Save Draft
                </button>
                <button
                  type="button"
                  onClick={handleSubmitVerification}
                  className="flex items-center justify-center gap-1.5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors shadow-sm shadow-emerald-100"
                >
                  <FileCheck className="w-3.5 h-3.5" />
                  Submit to QC
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------ STEP 3: OUTCOME SELECTION MODAL ------------------ */}
      <Dialog open={showOutcomeModal} onOpenChange={setShowOutcomeModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-extrabold text-base">Select Verification Outcome</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              Please select the verification outcome before proceeding.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 border border-emerald-100 bg-emerald-50/30 rounded-xl space-y-2 text-left">
              <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm">
                <CheckCircle className="w-5 h-5 text-emerald-650" />
                Positive Verification
              </div>
              <p className="text-xs text-slate-550 leading-normal">
                Select if the applicant lives/works at the address and all details match physical verification checks.
              </p>
              <Button
                type="button"
                onClick={() => handleOutcomeSelect('positive')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2 font-bold text-xs h-9 shadow-sm"
              >
                Proceed with Positive Verification
              </Button>
            </div>

            <div className="p-4 border border-rose-100 bg-rose-50/20 rounded-xl space-y-2 text-left">
              <div className="flex items-center gap-2 text-rose-700 font-bold text-sm">
                <XCircle className="w-5 h-5 text-rose-655" />
                Negative Verification
              </div>
              <p className="text-xs text-slate-555 leading-normal">
                Select if the door was locked, address was untraceable, applicant shifted, or refused information.
              </p>
              <Button
                type="button"
                onClick={() => handleOutcomeSelect('negative')}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white gap-2 font-bold text-xs h-9 shadow-sm"
              >
                Proceed with Negative Verification
              </Button>
            </div>
          </div>
          <DialogFooter className="border-t border-slate-100 pt-3 flex justify-end">
            <Button
              variant="ghost"
              onClick={() => setShowOutcomeModal(false)}
              className="text-slate-500 hover:bg-slate-50 text-xs h-9"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------ GEOTAGGING MANUAL TELEMETRY MODAL ------------------ */}
      {geotagModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col justify-between text-slate-800">
            <div className="bg-white border-b border-slate-100 p-5 px-6 flex justify-between items-center">
              <div>
                <h3 className="text-base font-extrabold text-slate-900">
                  Manual Telemetry GPS Editor
                </h3>
                <p className="text-[10px] text-slate-400 font-medium">
                  Type latitude/longitude parameters or click on map visualizer to resolve custom bounds.
                </p>
              </div>
              <button 
                type="button"
                onClick={() => setGeotagModalOpen(false)}
                className="p-1 border border-slate-200 hover:bg-slate-50 text-slate-400 hover:text-slate-700 rounded-lg transition-colors bg-white shadow-sm"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 block">
                  Interactive Grid Mapped Coordinates Plane (Click to set pin location)
                </label>
                <div 
                  onClick={handleMapGridClick}
                  className="h-48 border border-slate-200 rounded-xl relative overflow-hidden bg-slate-50 flex items-center justify-center border-dashed cursor-crosshair group shadow-inner"
                >
                  <div className="absolute inset-0 bg-[linear-gradient(to_right,#cbd5e1_1px,transparent_1px),linear-gradient(to_bottom,#cbd5e1_1px,transparent_1px)] bg-[size:16px_16px] opacity-25"></div>
                  <div className="absolute w-40 h-40 rounded-full border border-blue-200/50 bg-blue-50/10 z-0"></div>
                  <div className="absolute w-20 h-20 rounded-full border border-blue-200 bg-blue-50/20 z-0"></div>
                  <div className="relative z-10 flex flex-col items-center select-none group-hover:scale-105 transition-transform duration-200">
                    <MapPin className="w-9 h-9 text-blue-600 fill-blue-100" />
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600/30 blur-[1px] absolute -bottom-1"></div>
                  </div>
                  <span className="absolute top-2 right-3 text-[9px] bg-slate-200/80 border border-slate-300 text-slate-605 px-2 py-0.5 rounded font-semibold select-none z-10">
                    Map Offset Simulator
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 block">
                    Latitude Coordinate
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={tempLat}
                    onChange={(e) => setTempLat(e.target.value)}
                    className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-700 font-bold"
                    placeholder="23.0225"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 block">
                    Longitude Coordinate
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={tempLng}
                    onChange={(e) => setTempLng(e.target.value)}
                    className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-700 font-bold"
                    placeholder="72.5714"
                  />
                </div>
              </div>

              <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100 flex items-start gap-2.5 text-xs leading-normal">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-blue-700 font-medium">
                  <strong>Telemetry Specs Note:</strong> These custom geocoded coordinates are watermarked permanently onto photo evidence watermarking overlays to meet strict compliance audits.
                </div>
              </div>
            </div>

            <div className="bg-slate-50 border-t border-slate-100 p-4 px-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setGeotagModalOpen(false)}
                className="px-4 py-2 border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold rounded-lg text-xs transition-colors bg-white shadow-sm"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={saveGeotagDetails}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-extrabold transition-colors shadow-sm"
              >
                Apply Coordinates
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Route Planner optimized sequence Modal */}
      <Dialog open={showRoutePlanner} onOpenChange={setShowRoutePlanner}>
        <DialogContent className="bg-white border-slate-200 max-w-xl text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Compass className="w-5 h-5 text-blue-600" />
              Route Optimization Planner
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Generates travel order sequence with Google Maps optimized pathways.
            </DialogDescription>
          </DialogHeader>

          {isGeneratingRoute ? (
            <div className="py-12 flex flex-col items-center justify-center space-y-3">
              <RefreshCw className="w-10 h-10 animate-spin text-blue-600" />
              <p className="text-sm font-semibold text-slate-600">Generating optimized route sequencing...</p>
            </div>
          ) : (
            <div className="py-4 space-y-5">
              {/* Map Canvas Visual Frame */}
              <div className="relative aspect-video rounded-xl overflow-hidden border border-slate-200 bg-slate-50 shadow-2xs">
                {/* Embed OpenStreetMap layout */}
                <iframe
                  title="Route Plan Map"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=79.05%2C21.10%2C79.15%2C21.20&amp;layer=mapnik&amp;marker=21.14%2C79.08"
                  className="w-full h-full border-none opacity-85"
                />
                <div className="absolute inset-0 bg-blue-900/10 pointer-events-none" />
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-xs px-2.5 py-1 rounded-md text-[10px] font-black text-slate-800 shadow-2xs uppercase tracking-wider flex items-center gap-1.5 border border-slate-100">
                  <Map className="w-3.5 h-3.5 text-blue-600" />
                  Map Integration Enabled
                </div>
              </div>

              {/* Route sequence stops list */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Optimized Sequence Order</h4>
                  <span className="text-xs text-slate-500 font-bold">
                    Est. Time: {routeStops.length * 35} mins • Dist: {routeStops.length * 4.2} km
                  </span>
                </div>
                
                <ScrollArea className="max-h-[220px] pr-1">
                  <div className="space-y-2">
                    {routeStops.map((stop) => (
                      <div key={stop.refNumber} className="flex gap-3 p-3 rounded-lg border border-slate-100 bg-slate-50 hover:bg-slate-100/50 transition-colors text-xs items-center">
                        <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center font-bold text-white shrink-0 shadow-3xs">
                          {stop.sequence}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-bold text-slate-900 truncate">{stop.applicantName}</p>
                            <span className="text-[10px] text-blue-600 font-semibold uppercase">{stop.refNumber}</span>
                          </div>
                          <p className="text-slate-500 truncate mt-0.5">{stop.address}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-bold text-slate-800">{stop.eta}</p>
                          <p className="text-[10px] text-slate-400 font-semibold mt-0.5">{stop.distance}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              <DialogFooter className="gap-2 pt-2 border-t border-slate-100">
                <Button variant="outline" size="sm" onClick={() => setShowRoutePlanner(false)}>
                  Close Plan
                </Button>
                <Button 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-700 gap-2 font-bold"
                  onClick={() => {
                    const firstStop = routeStops[0];
                    if (firstStop) {
                      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(firstStop.address)}`, '_blank');
                    }
                  }}
                >
                  <Navigation className="w-4 h-4" />
                  Start Directions
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Extra KM Request dialog */}
      <Dialog open={showKmDialog} onOpenChange={setShowKmDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Request Extra KMs</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Approved extra KMs are calculated at <strong className="text-slate-900 font-bold">₹2/km</strong>. Your request will be sent to your manager for approval.
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Distance Driven (Extra)</label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder="e.g. 15"
                  value={extraKmRequest}
                  onChange={(e) => setExtraKmRequest(e.target.value)}
                  className="pr-10 bg-slate-50/50 border-slate-200"
                />
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-slate-500 text-sm">km</div>
              </div>
              {extraKmRequest && (
                <p className="text-sm text-emerald-500 mt-2">Estimated Payout: ₹{Number(extraKmRequest) * 2}</p>
              )}
            </div>
            <div className="space-y-2 pt-2">
              <label className="text-sm font-medium text-slate-700">Reason</label>
              <Input
                placeholder="E.g., Client shifted to a different address..."
                value={extraKmReason}
                onChange={(e) => setExtraKmReason(e.target.value)}
                className="bg-slate-50/50 border-slate-200"
              />
            </div>
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 font-bold"
              disabled={!extraKmRequest || Number(extraKmRequest) <= 0 || !extraKmReason}
              onClick={() => {
                const newReq = {
                  id: `EC-${Date.now().toString().slice(-4)}`,
                  caseId: myPendingCases[0]?.id || 'CASE001',
                  caseRef: myPendingCases[0]?.refNumber || 'ADV-2024-00001',
                  requestedBy: {
                    id: currentUserId,
                    name: activeUser.name,
                    avatar: activeUser.avatar,
                    role: 'field_executive'
                  },
                  amount: Number(extraKmRequest) * 2,
                  reason: `Extra KMs: ${extraKmReason} (${extraKmRequest} KMs)`,
                  status: 'pending',
                  date: new Date().toISOString()
                };
                submitExtraCostRequest(newReq);
                setExtraKmRequest('');
                setExtraKmReason('');
                setShowKmDialog(false);
                alert('Extra KM cost request submitted successfully.');
              }}
            >
              Submit Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extra Cost Request Dialog */}
      <Dialog open={showExtraCostModal} onOpenChange={setShowExtraCostModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-blue-605" />
              Request Additional Cost Approval
            </DialogTitle>
          </DialogHeader>
          {extraCostCase && (
            <div className="space-y-4 py-3">
              <div className="bg-slate-50 p-4 border border-slate-150 rounded-2xl text-xs space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500 font-semibold">Case ID:</span>
                  <span className="font-bold text-slate-800">{extraCostCase.refNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-semibold">Applicant Name:</span>
                  <span className="font-bold text-slate-800">{extraCostCase.applicantName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-semibold">Verification Type:</span>
                  <span className="font-medium text-slate-700">{extraCostCase.verificationType || extraCostCase.product?.name || 'Residence Verification'}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Expense Type</Label>
                <select
                  value={expenseType}
                  onChange={(e) => setExpenseType(e.target.value)}
                  className="w-full rounded-xl bg-white border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-800 outline-none cursor-pointer focus:ring-2 focus:ring-blue-500/25"
                >
                  <option value="Fuel Charges">Fuel Charges</option>
                  <option value="Toll Charges">Toll Charges</option>
                  <option value="Parking Charges">Parking Charges</option>
                  <option value="Outstation Travel">Outstation Travel</option>
                  <option value="Remote Area Visit">Remote Area Visit</option>
                  <option value="Public Transport">Public Transport</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Requested Amount (₹)</Label>
                <Input
                  type="number"
                  placeholder="e.g. 500"
                  value={requestedAmount}
                  onChange={(e) => setRequestedAmount(e.target.value)}
                  className="rounded-xl border-slate-200 text-xs bg-slate-50/50"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Reason / Justification <span className="text-red-500">*</span></Label>
                <textarea
                  placeholder="Explain why this additional cost is required..."
                  value={extraCostReason}
                  onChange={(e) => setExtraCostReason(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 p-3 text-xs bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500/25 min-h-[90px]"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Supporting Evidence (Optional)</Label>
                <div className="border border-dashed border-slate-200 rounded-lg p-4 text-center bg-slate-50 hover:bg-slate-100/50 transition-colors relative cursor-pointer">
                  <input
                    type="file"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setEvidenceFileName(e.target.files[0].name);
                      }
                    }}
                  />
                  <Upload className="w-6 h-6 mx-auto text-slate-400 mb-1.5" />
                  <p className="text-xs text-slate-700 font-semibold">
                    {evidenceFileName ? `Attached: ${evidenceFileName}` : 'Upload receipt, bill, photo, or PDF'}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Click to browse or drop file here</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="border-t border-slate-100 pt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowExtraCostModal(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl text-xs px-4 py-2">
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (!requestedAmount || Number(requestedAmount) <= 0 || !extraCostReason.trim()) {
                  toast.error('Please enter a valid amount and mandatory reason.');
                  return;
                }
                const newReq = {
                  id: `EC-${Date.now().toString().slice(-4)}`,
                  caseId: extraCostCase.id,
                  caseRef: extraCostCase.refNumber,
                  requestedBy: {
                    id: currentUserId,
                    name: activeUser.name,
                    avatar: activeUser.avatar,
                    role: currentRole
                  },
                  amount: Number(requestedAmount),
                  expenseType: expenseType,
                  reason: extraCostReason,
                  status: 'pending',
                  date: new Date().toISOString(),
                  evidence: evidenceFileName || null
                };
                submitExtraCostRequest(newReq);
                setShowExtraCostModal(false);
                setRequestedAmount('');
                setExtraCostReason('');
                setEvidenceFileName('');
                toast.success('Additional cost request submitted successfully!');
              }} 
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs px-4 py-2"
            >
              Submit Extra Cost Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Request Dialog */}
      <Dialog open={showReturnRequestModal} onOpenChange={setShowReturnRequestModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-500 animate-pulse" />
              Request Case Return (Non-Working Area)
            </DialogTitle>
          </DialogHeader>
          {returnCase && (
            <div className="space-y-4 py-3">
              <div className="bg-slate-50 p-4 border border-slate-150 rounded-2xl text-xs space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500 font-semibold">Case ID:</span>
                  <span className="font-bold text-slate-800">{returnCase.refNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-semibold">Applicant Name:</span>
                  <span className="font-bold text-slate-800">{returnCase.applicantName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-semibold">Address:</span>
                  <span className="font-medium text-slate-700 truncate max-w-[200px]" title={returnCase.address}>{returnCase.address}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Return Reason</Label>
                <select
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  className="w-full rounded-xl bg-white border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-808 outline-none cursor-pointer focus:ring-2 focus:ring-rose-500/25"
                >
                  <option value="Outside Serviceable Area">Outside Serviceable Area</option>
                  <option value="Beyond Operational Limits">Beyond Operational Limits</option>
                  <option value="Restricted Location">Restricted Location</option>
                  <option value="Distance Constraints">Distance Constraints</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Detailed Justification <span className="text-red-500">*</span></Label>
                <textarea
                  placeholder="Explain why this case cannot be verified (mandatory)..."
                  value={returnComments}
                  onChange={(e) => setReturnComments(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 p-3 text-xs bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-rose-500/25 min-h-[90px]"
                />
              </div>
            </div>
          )}

          <DialogFooter className="border-t border-slate-100 pt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowReturnRequestModal(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl text-xs px-4 py-2">
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (!returnComments.trim()) {
                  toast.error('Detailed justification is mandatory.');
                  return;
                }
                const newReq = {
                  id: `RR-${Date.now().toString().slice(-4)}`,
                  caseId: returnCase.id,
                  refNumber: returnCase.refNumber,
                  applicantName: returnCase.applicantName,
                  requestedBy: {
                    id: currentUserId,
                    name: activeUser.name,
                    role: currentRole
                  },
                  reason: returnReason,
                  comments: returnComments,
                  status: 'pending',
                  date: new Date().toISOString(),
                  timeline: [
                    { role: ROLE_LABELS[currentRole] || currentRole, action: 'Submitted Return Request', user: activeUser.name, date: new Date().toISOString().split('T')[0], comment: returnComments }
                  ]
                };
                submitReturnRequest(newReq);
                setShowReturnRequestModal(false);
                setReturnComments('');
                setSelectedCase(null); // close details modal
                toast.warning('Case Return request submitted successfully. Superiors have been notified.');
              }} 
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs px-4 py-2"
            >
              Submit Return Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}
